// Client-side data-access layer talking directly to Firestore.
// This replaces the FastAPI/Mongo backend: every function here mirrors the
// authorization + business logic that used to live in backend/server.py.
// Enforcement of "who can do what" happens in firestore.rules, not here —
// these functions assume the caller has already been allowed by the rules.
import { db } from "./firebase";
import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  query, where, runTransaction, increment, writeBatch,
} from "firebase/firestore";

export const DEFAULT_PERMISSIONS = {
  can_report_damage: true,
  can_request_tools: true,
};

const nowIso = () => new Date().toISOString();
const sortByDesc = (items, key) => [...items].sort((a, b) => (b[key] || "").localeCompare(a[key] || ""));
const cleanUpdate = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== null && v !== undefined));

// -----------------------------------------------------------------------------
// Categories
// -----------------------------------------------------------------------------
export async function listCategories() {
  const snap = await getDocs(collection(db, "categories"));
  return snap.docs.map((d) => d.data());
}

export async function createCategory({ name, description }, user) {
  const existing = await getDocs(query(collection(db, "categories"), where("name", "==", name)));
  if (!existing.empty) throw new Error("Category name already exists");
  const ref = doc(collection(db, "categories"));
  const data = { id: ref.id, name, description: description || "", created_by: user.id, created_at: nowIso() };
  await setDoc(ref, data);
  return data;
}

export async function updateCategory(id, { name, description }) {
  await updateDoc(doc(db, "categories", id), { name, description: description || "" });
}

export async function deleteCategory(id) {
  const inUse = await getDocs(query(collection(db, "tools"), where("category_id", "==", id)));
  if (!inUse.empty) throw new Error("Category in use by tools");
  await deleteDoc(doc(db, "categories", id));
}

// Non-admin self-serve category creation, gated on admin approval.
export async function listCategoryRequests(user) {
  const constraints = user.role !== "admin" ? [where("requested_by", "==", user.id)] : [];
  const snap = await getDocs(query(collection(db, "category_requests"), ...constraints));
  return sortByDesc(snap.docs.map((d) => d.data()), "created_at");
}

export async function requestCategory({ name }, user) {
  const ref = doc(collection(db, "category_requests"));
  const data = {
    id: ref.id, name, requested_by: user.id, requested_by_name: user.name,
    status: "pending", created_at: nowIso(), reviewed_by: null, reviewed_at: null,
  };
  await setDoc(ref, data);
  return data;
}

export async function reviewCategoryRequest(id, action, admin) {
  const ref = doc(db, "category_requests", id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Request not found");
  // Create the real category first -- if this throws (e.g. a duplicate name),
  // the request stays pending instead of landing in a broken "approved but
  // doesn't actually exist" state.
  if (action === "approve") {
    await createCategory({ name: snap.data().name, description: "" }, admin);
  }
  await updateDoc(ref, {
    status: action === "approve" ? "approved" : "rejected",
    reviewed_by: admin.id, reviewed_at: nowIso(),
  });
}

// -----------------------------------------------------------------------------
// Locations
// -----------------------------------------------------------------------------
export async function listLocations() {
  const snap = await getDocs(collection(db, "locations"));
  return snap.docs.map((d) => d.data());
}

export async function createLocation({ name, description, bins }, user) {
  const existing = await getDocs(query(collection(db, "locations"), where("name", "==", name)));
  if (!existing.empty) throw new Error("Location name already exists");
  const ref = doc(collection(db, "locations"));
  const data = { id: ref.id, name, description: description || "", bins: bins || [], created_by: user.id, created_at: nowIso() };
  await setDoc(ref, data);
  return data;
}

export async function updateLocation(id, { name, description, bins }) {
  await updateDoc(doc(db, "locations", id), { name, description: description || "", bins: bins || [] });
}

export async function deleteLocation(id) {
  const inUse = await getDocs(query(collection(db, "tools"), where("location_id", "==", id)));
  if (!inUse.empty) throw new Error("Location in use by tools");
  await deleteDoc(doc(db, "locations", id));
}

// Non-admin self-serve location creation, gated on admin approval.
export async function listLocationRequests(user) {
  const constraints = user.role !== "admin" ? [where("requested_by", "==", user.id)] : [];
  const snap = await getDocs(query(collection(db, "location_requests"), ...constraints));
  return sortByDesc(snap.docs.map((d) => d.data()), "created_at");
}

export async function requestLocation({ name }, user) {
  const ref = doc(collection(db, "location_requests"));
  const data = {
    id: ref.id, name, requested_by: user.id, requested_by_name: user.name,
    status: "pending", created_at: nowIso(), reviewed_by: null, reviewed_at: null,
  };
  await setDoc(ref, data);
  return data;
}

export async function reviewLocationRequest(id, action, admin) {
  const ref = doc(db, "location_requests", id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Request not found");
  if (action === "approve") {
    await createLocation({ name: snap.data().name, description: "", bins: [] }, admin);
  }
  await updateDoc(ref, {
    status: action === "approve" ? "approved" : "rejected",
    reviewed_by: admin.id, reviewed_at: nowIso(),
  });
}

// -----------------------------------------------------------------------------
// Tools
// -----------------------------------------------------------------------------
// Note: Firestore can't do substring/full-text search like Mongo's $regex, so
// the `q` filter runs client-side after fetching. Fine at shop-inventory scale;
// revisit (e.g. a dedicated search index) if the catalog grows very large.
export async function listTools({ q, category_id, location_id } = {}) {
  const constraints = [];
  if (category_id) constraints.push(where("category_id", "==", category_id));
  if (location_id) constraints.push(where("location_id", "==", location_id));
  const snap = await getDocs(query(collection(db, "tools"), ...constraints));
  let tools = sortByDesc(snap.docs.map((d) => d.data()), "created_at");
  if (q) {
    const needle = q.toLowerCase();
    tools = tools.filter((t) =>
      (t.name || "").toLowerCase().includes(needle) ||
      (t.tool_code || "").toLowerCase().includes(needle) ||
      (t.description || "").toLowerCase().includes(needle)
    );
  }
  return tools;
}

export async function getTool(id) {
  const snap = await getDoc(doc(db, "tools", id));
  if (!snap.exists()) throw new Error("Tool not found");
  const historySnap = await getDocs(query(collection(db, "transactions"), where("tool_id", "==", id)));
  const history = sortByDesc(historySnap.docs.map((d) => d.data()), "issue_date");
  return { ...snap.data(), history };
}

export async function createTool(body, admin) {
  const existing = await getDocs(query(collection(db, "tools"), where("tool_code", "==", body.tool_code)));
  if (!existing.empty) throw new Error("Tool code already exists");
  const ref = doc(collection(db, "tools"));
  const data = {
    ...body, id: ref.id, available_quantity: body.quantity, status: "available",
    created_by: admin.id, created_at: nowIso(),
  };
  await setDoc(ref, data);
  return data;
}

export async function updateTool(id, body) {
  const snap = await getDoc(doc(db, "tools", id));
  if (!snap.exists()) throw new Error("Tool not found");
  const tool = snap.data();
  const update = cleanUpdate(body);
  if ("quantity" in update) {
    const delta = update.quantity - (tool.quantity || 0);
    update.available_quantity = Math.max(0, (tool.available_quantity || 0) + delta);
  }
  await updateDoc(doc(db, "tools", id), update);
}

export async function deleteTool(id) {
  await deleteDoc(doc(db, "tools", id));
  const txnSnap = await getDocs(query(collection(db, "transactions"), where("tool_id", "==", id)));
  if (!txnSnap.empty) {
    const batch = writeBatch(db);
    txnSnap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }
}

export async function lookupToolByCode(code) {
  const snap = await getDocs(query(collection(db, "tools"), where("tool_code", "==", code)));
  if (snap.empty) throw new Error("Tool not found");
  return snap.docs[0].data();
}

// -----------------------------------------------------------------------------
// Transactions (Issue / Receive)
// -----------------------------------------------------------------------------
export async function listTransactions(user, { status_filter, user_id, tool_id } = {}) {
  const constraints = [];
  if (user.role !== "admin") constraints.push(where("user_id", "==", user.id));
  else if (user_id) constraints.push(where("user_id", "==", user_id));
  if (status_filter) constraints.push(where("status", "==", status_filter));
  if (tool_id) constraints.push(where("tool_id", "==", tool_id));
  const snap = await getDocs(query(collection(db, "transactions"), ...constraints));
  return sortByDesc(snap.docs.map((d) => d.data()), "issue_date");
}

export async function issueTool({ tool_id, user_id, quantity, due_date, notes }, admin, targetUser) {
  const txnRef = doc(collection(db, "transactions"));
  const toolRef = doc(db, "tools", tool_id);

  const record = await runTransaction(db, async (t) => {
    const toolSnap = await t.get(toolRef);
    if (!toolSnap.exists()) throw new Error("Tool not found");
    const tool = toolSnap.data();
    if ((tool.available_quantity || 0) < quantity) throw new Error("Insufficient quantity available");
    const txn = {
      id: txnRef.id, tool_id, tool_name: tool.name, tool_code: tool.tool_code,
      user_id, user_name: targetUser.name, issued_by: admin.id, issued_by_name: admin.name,
      quantity, issue_date: nowIso(), due_date, return_date: null,
      condition_on_return: null, status: "issued", notes: notes || "",
    };
    t.set(txnRef, txn);
    t.update(toolRef, { available_quantity: tool.available_quantity - quantity });
    return txn;
  });

  // Bump usage counters on active maintenance schedules (best-effort, separate from
  // the atomic quantity check above since Firestore transactions can't run queries).
  const schedSnap = await getDocs(query(
    collection(db, "maintenance_schedules"),
    where("tool_id", "==", tool_id),
    where("active", "==", true),
  ));
  if (!schedSnap.empty) {
    const batch = writeBatch(db);
    schedSnap.docs.forEach((d) => batch.update(d.ref, { uses_since_service: increment(quantity) }));
    await batch.commit();
  }

  return record;
}

export async function returnTool(txnId, { condition_on_return, notes }, admin) {
  const txnRef = doc(db, "transactions", txnId);

  const result = await runTransaction(db, async (t) => {
    const txnSnap = await t.get(txnRef);
    if (!txnSnap.exists()) throw new Error("Transaction not found");
    const txn = txnSnap.data();
    if (txn.status === "returned") throw new Error("Already returned");
    const toolRef = doc(db, "tools", txn.tool_id);
    const toolSnap = await t.get(toolRef);

    t.update(txnRef, {
      status: "returned",
      return_date: nowIso(),
      condition_on_return,
      notes: (txn.notes || "") + (notes ? ` | Return: ${notes}` : ""),
    });
    if (toolSnap.exists()) {
      const tool = toolSnap.data();
      t.update(toolRef, { available_quantity: (tool.available_quantity || 0) + txn.quantity });
    }
    return txn;
  });

  if (condition_on_return === "damaged") {
    const reportRef = doc(collection(db, "damage_reports"));
    await setDoc(reportRef, {
      id: reportRef.id, tool_id: result.tool_id, tool_name: result.tool_name,
      reported_by: admin.id, reported_by_name: admin.name,
      description: `Reported on return from ${result.user_name}. ${notes || ""}`,
      severity: "moderate", photo_urls: [], status: "pending", created_at: nowIso(),
      reviewed_by: null, reviewed_at: null, admin_notes: "",
    });
  }
}

// -----------------------------------------------------------------------------
// Damage Reports
// -----------------------------------------------------------------------------
export async function listDamageReports(user) {
  const constraints = user.role !== "admin" ? [where("reported_by", "==", user.id)] : [];
  const snap = await getDocs(query(collection(db, "damage_reports"), ...constraints));
  return sortByDesc(snap.docs.map((d) => d.data()), "created_at");
}

export async function createDamageReport(body, user) {
  if (user.permissions?.can_report_damage === false) throw new Error("Permission denied");
  const toolSnap = await getDoc(doc(db, "tools", body.tool_id));
  if (!toolSnap.exists()) throw new Error("Tool not found");
  const tool = toolSnap.data();
  const ref = doc(collection(db, "damage_reports"));
  const data = {
    id: ref.id, tool_id: body.tool_id, tool_name: tool.name,
    reported_by: user.id, reported_by_name: user.name,
    description: body.description, severity: body.severity || "moderate",
    photo_urls: body.photo_urls || [], status: "pending", created_at: nowIso(),
    reviewed_by: null, reviewed_at: null, admin_notes: "",
  };
  await setDoc(ref, data);
  return data;
}

export async function reviewDamageReport(id, action, admin_notes, admin) {
  const ref = doc(db, "damage_reports", id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Report not found");
  const newStatus = action === "approve" ? "approved" : "rejected";
  await updateDoc(ref, { status: newStatus, reviewed_by: admin.id, reviewed_at: nowIso(), admin_notes: admin_notes || "" });
  if (action === "approve") {
    await updateDoc(doc(db, "tools", snap.data().tool_id), { condition: "poor" });
  }
}

// -----------------------------------------------------------------------------
// Tool Requests
// -----------------------------------------------------------------------------
export async function listToolRequests(user) {
  const constraints = user.role !== "admin" ? [where("requested_by", "==", user.id)] : [];
  const snap = await getDocs(query(collection(db, "tool_requests"), ...constraints));
  return sortByDesc(snap.docs.map((d) => d.data()), "created_at");
}

// A request is always a checkout of an existing tool to the requester — the
// old "propose a brand-new tool" fallback was removed (that stays admin-only
// via the Tools page's Add Tool form, see memory/toolcrew_issue_request_redesign.md).
export async function createToolRequest(body, user) {
  if (user.permissions?.can_request_tools === false) throw new Error("Permission denied");
  const ref = doc(collection(db, "tool_requests"));
  const data = {
    id: ref.id, requested_by: user.id, requested_by_name: user.name,
    tool_id: body.tool_id, tool_name: body.tool_name, category_id: body.category_id || "",
    quantity: body.quantity, justification: body.justification, urgency: body.urgency || "normal",
    status: "pending", created_at: nowIso(), reviewed_by: null, reviewed_at: null, admin_notes: "",
  };
  await setDoc(ref, data);
  return data;
}

// Approving auto-issues the tool (same issueTool transaction the manual Issue
// flow uses) with an admin-supplied due date — if there's not enough quantity
// available by review time, issueTool throws and the request stays pending
// instead of silently flipping to "approved" with nothing issued.
export async function reviewToolRequest(id, action, admin, { due_date, notes, admin_notes } = {}) {
  const ref = doc(db, "tool_requests", id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Request not found");
  const reqData = snap.data();
  if (action === "approve") {
    await issueTool(
      { tool_id: reqData.tool_id, user_id: reqData.requested_by, quantity: reqData.quantity, due_date, notes: notes || "" },
      admin,
      { name: reqData.requested_by_name },
    );
  }
  await updateDoc(ref, {
    status: action === "approve" ? "approved" : "rejected",
    reviewed_by: admin.id, reviewed_at: nowIso(), admin_notes: admin_notes || "",
  });
}

// -----------------------------------------------------------------------------
// Preventive Maintenance
// -----------------------------------------------------------------------------
export function computeMaintenanceStatus(sched) {
  const now = new Date();
  const interval_days = sched.interval_days || 0;
  const interval_uses = sched.interval_uses || 0;
  const uses_since = sched.uses_since_service || 0;
  const last = sched.last_serviced_at;

  let time_overdue = false, usage_overdue = false;
  let days_remaining = null, uses_remaining = null, next_due_date = null;

  if (interval_days && last) {
    const nextDueDt = new Date(new Date(last).getTime() + interval_days * 86400000);
    next_due_date = nextDueDt.toISOString();
    const deltaDays = (nextDueDt.getTime() - now.getTime()) / 86400000;
    days_remaining = Math.round(deltaDays * 10) / 10;
    time_overdue = deltaDays < 0;
  }
  if (interval_uses) {
    uses_remaining = interval_uses - uses_since;
    usage_overdue = uses_since >= interval_uses;
  }
  const is_overdue = time_overdue || usage_overdue;
  const isDueSoonRaw =
    (days_remaining !== null && days_remaining >= 0 && days_remaining <= 3) ||
    (uses_remaining !== null && uses_remaining >= 0 && uses_remaining <= 1);

  return {
    ...sched, next_due_date, days_remaining, uses_remaining,
    is_overdue, is_due_soon: isDueSoonRaw && !is_overdue, time_overdue, usage_overdue,
  };
}

export async function listMaintenanceSchedules({ tool_id, only_active } = {}) {
  const constraints = [];
  if (tool_id) constraints.push(where("tool_id", "==", tool_id));
  if (only_active) constraints.push(where("active", "==", true));
  const snap = await getDocs(query(collection(db, "maintenance_schedules"), ...constraints));
  let schedules = sortByDesc(snap.docs.map((d) => d.data()), "created_at");

  const toolIds = [...new Set(schedules.map((s) => s.tool_id))];
  const toolsMap = {};
  await Promise.all(toolIds.map(async (tid) => {
    const t = await getDoc(doc(db, "tools", tid));
    toolsMap[tid] = t.exists() ? t.data() : {};
  }));
  schedules = schedules.map((s) => ({ ...s, tool_name: toolsMap[s.tool_id]?.name, tool_code: toolsMap[s.tool_id]?.tool_code }));
  return schedules.map(computeMaintenanceStatus);
}

export async function createMaintenanceSchedule(body, admin) {
  const toolSnap = await getDoc(doc(db, "tools", body.tool_id));
  if (!toolSnap.exists()) throw new Error("Tool not found");
  if (!body.interval_days && !body.interval_uses) throw new Error("Set at least one of interval_days or interval_uses");
  const ref = doc(collection(db, "maintenance_schedules"));
  const data = {
    id: ref.id, tool_id: body.tool_id, name: body.name, description: body.description || "",
    interval_days: body.interval_days || 0, interval_uses: body.interval_uses || 0,
    active: body.active !== false, last_serviced_at: nowIso(),
    last_serviced_by: admin.id, last_serviced_by_name: admin.name,
    uses_since_service: 0, created_by: admin.id, created_at: nowIso(),
  };
  await setDoc(ref, data);
  return computeMaintenanceStatus(data);
}

export async function updateMaintenanceSchedule(id, update) {
  const clean = cleanUpdate(update);
  if (Object.keys(clean).length === 0) throw new Error("Nothing to update");
  await updateDoc(doc(db, "maintenance_schedules", id), clean);
}

export async function deleteMaintenanceSchedule(id) {
  await deleteDoc(doc(db, "maintenance_schedules", id));
  const eventsSnap = await getDocs(query(collection(db, "maintenance_events"), where("schedule_id", "==", id)));
  if (!eventsSnap.empty) {
    const batch = writeBatch(db);
    eventsSnap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }
}

export async function logMaintenanceService(scheduleId, notes, user) {
  const schedRef = doc(db, "maintenance_schedules", scheduleId);
  const schedSnap = await getDoc(schedRef);
  if (!schedSnap.exists()) throw new Error("Schedule not found");
  const sched = schedSnap.data();
  const eventRef = doc(collection(db, "maintenance_events"));
  const event = {
    id: eventRef.id, schedule_id: scheduleId, tool_id: sched.tool_id,
    serviced_by: user.id, serviced_by_name: user.name, serviced_at: nowIso(),
    uses_at_service: sched.uses_since_service || 0, notes: notes || "",
  };
  await setDoc(eventRef, event);
  await updateDoc(schedRef, {
    last_serviced_at: event.serviced_at, last_serviced_by: user.id,
    last_serviced_by_name: user.name, uses_since_service: 0,
  });
  return event;
}

// -----------------------------------------------------------------------------
// Dashboard Stats
// -----------------------------------------------------------------------------
export async function getDashboardStats(user) {
  const [toolsSnap, catsSnap, locsSnap] = await Promise.all([
    getDocs(collection(db, "tools")),
    getDocs(collection(db, "categories")),
    getDocs(collection(db, "locations")),
  ]);
  const tools = toolsSnap.docs.map((d) => d.data());
  const total_qty = tools.reduce((a, t) => a + (t.quantity || 0), 0);
  const avail_qty = tools.reduce((a, t) => a + (t.available_quantity || 0), 0);

  let pending_users = 0, pending_damage = 0, pending_requests = 0, active_transactions = 0, overdue = 0;
  const today = nowIso();

  // "Requests" spans every self-serve pending-approval collection — tool
  // procurement asks plus the category/location self-serve creates — so
  // admins see one true count of everything awaiting their review.
  if (user.role === "admin") {
    const [usersSnap, dmgSnap, reqSnap, catReqSnap, locReqSnap, txnSnap] = await Promise.all([
      getDocs(query(collection(db, "users"), where("status", "==", "pending"))),
      getDocs(query(collection(db, "damage_reports"), where("status", "==", "pending"))),
      getDocs(query(collection(db, "tool_requests"), where("status", "==", "pending"))),
      getDocs(query(collection(db, "category_requests"), where("status", "==", "pending"))),
      getDocs(query(collection(db, "location_requests"), where("status", "==", "pending"))),
      getDocs(query(collection(db, "transactions"), where("status", "==", "issued"))),
    ]);
    pending_users = usersSnap.size;
    pending_damage = dmgSnap.size;
    pending_requests = reqSnap.size + catReqSnap.size + locReqSnap.size;
    active_transactions = txnSnap.size;
    overdue = txnSnap.docs.filter((d) => (d.data().due_date || "") < today).length;
  } else {
    const [dmgSnap, reqSnap, catReqSnap, locReqSnap, txnSnap] = await Promise.all([
      getDocs(query(collection(db, "damage_reports"), where("reported_by", "==", user.id), where("status", "==", "pending"))),
      getDocs(query(collection(db, "tool_requests"), where("requested_by", "==", user.id), where("status", "==", "pending"))),
      getDocs(query(collection(db, "category_requests"), where("requested_by", "==", user.id), where("status", "==", "pending"))),
      getDocs(query(collection(db, "location_requests"), where("requested_by", "==", user.id), where("status", "==", "pending"))),
      getDocs(query(collection(db, "transactions"), where("user_id", "==", user.id), where("status", "==", "issued"))),
    ]);
    pending_damage = dmgSnap.size;
    pending_requests = reqSnap.size + catReqSnap.size + locReqSnap.size;
    active_transactions = txnSnap.size;
  }

  const schedSnap = await getDocs(query(collection(db, "maintenance_schedules"), where("active", "==", true)));
  let maintenance_overdue = 0, maintenance_due_soon = 0;
  schedSnap.docs.forEach((d) => {
    const st = computeMaintenanceStatus(d.data());
    if (st.is_overdue) maintenance_overdue += 1;
    else if (st.is_due_soon) maintenance_due_soon += 1;
  });

  return {
    total_tools: tools.length, total_qty, available_qty: avail_qty, issued_qty: total_qty - avail_qty,
    total_categories: catsSnap.size, total_locations: locsSnap.size,
    pending_users, pending_damage, pending_requests, active_transactions, overdue,
    maintenance_overdue, maintenance_due_soon, total_schedules: schedSnap.size,
  };
}

// -----------------------------------------------------------------------------
// Admin: User Management
// -----------------------------------------------------------------------------
export async function listUsers() {
  const snap = await getDocs(collection(db, "users"));
  return snap.docs.map((d) => d.data());
}

export async function approveUser(id) {
  await updateDoc(doc(db, "users", id), { status: "approved" });
}

export async function rejectUser(id) {
  await updateDoc(doc(db, "users", id), { status: "rejected" });
}

export async function setUserRole(id, role) {
  if (!["admin", "user"].includes(role)) throw new Error("Invalid role");
  await updateDoc(doc(db, "users", id), { role });
}

export async function setUserPermissions(id, permissions) {
  const snap = await getDoc(doc(db, "users", id));
  if (!snap.exists()) throw new Error("User not found");
  const merged = { ...DEFAULT_PERMISSIONS, ...(snap.data().permissions || {}), ...(permissions || {}) };
  await updateDoc(doc(db, "users", id), { permissions: merged });
  return merged;
}

// Only removes the Firestore profile — deleting the actual Firebase Auth account
// requires the Admin SDK (Firebase Console, or a future Cloud Function), since the
// client SDK can only ever delete the currently signed-in user's own account.
export async function deleteUserProfile(id, requesterId) {
  if (id === requesterId) throw new Error("Cannot delete yourself");
  await deleteDoc(doc(db, "users", id));
}

// -----------------------------------------------------------------------------
// CSV Export (client-generated, admin only)
// -----------------------------------------------------------------------------
function csvRow(vals) {
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return vals.map(esc).join(",") + "\n";
}

function downloadCSV(content, filename) {
  const blob = new Blob([content], { type: "text/csv" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export async function exportToolsCSV() {
  const [tools, cats, locs] = await Promise.all([listTools(), listCategories(), listLocations()]);
  const catMap = Object.fromEntries(cats.map((c) => [c.id, c.name]));
  const locMap = Object.fromEntries(locs.map((l) => [l.id, l.name]));
  let out = csvRow(["Tool Code", "Name", "Category", "Location", "Bin", "Quantity", "Available", "Condition", "Description", "Created"]);
  for (const t of tools) {
    out += csvRow([t.tool_code, t.name, catMap[t.category_id] || "-", locMap[t.location_id] || "-", t.bin, t.quantity, t.available_quantity, t.condition, t.description, t.created_at]);
  }
  downloadCSV(out, "tools_inventory.csv");
}

export async function exportTransactionsCSV(user) {
  const txns = await listTransactions(user);
  let out = csvRow(["Tool", "Tool Code", "User", "Qty", "Issued", "Due", "Returned", "Condition On Return", "Status", "Notes"]);
  for (const t of txns) {
    out += csvRow([t.tool_name, t.tool_code, t.user_name, t.quantity, t.issue_date, t.due_date, t.return_date || "", t.condition_on_return || "", t.status, t.notes]);
  }
  downloadCSV(out, "transactions.csv");
}

export async function exportDamageReportsCSV(user) {
  const reports = await listDamageReports(user);
  let out = csvRow(["Tool", "Reported By", "Severity", "Status", "Description", "Photos", "Created", "Reviewed", "Admin Notes"]);
  for (const r of reports) {
    out += csvRow([r.tool_name, r.reported_by_name, r.severity, r.status, r.description, (r.photo_urls || []).join("; "), r.created_at, r.reviewed_at || "", r.admin_notes]);
  }
  downloadCSV(out, "damage_reports.csv");
}
